/**
 * WCAG 2.1 relative luminance and contrast ratios.
 *
 * Exported so the theme's own tests can prove every semantic ramp is readable on the surfaces it actually renders on,
 * rather than asserting that a hex string did not change. Product code does not call these: it consumes semantic roles
 * and Mantine variables (docs/UI_STANDARDS.md).
 */

export type Rgb = readonly [number, number, number];

export function hexToRgb(hex: string): Rgb {
  const value = hex.replace("#", "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;
  const n = Number.parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG relative luminance of an sRGB color. */
export function relativeLuminance(hex: string): number {
  const channels = hexToRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  }) as unknown as Rgb;
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** WCAG contrast ratio between two opaque colors, from 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [light, dark] = la > lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}

/**
 * The label color Mantine's `autoContrast` picks for a filled surface, given the theme's luminance threshold.
 * Mirrors Mantine's own rule so tests can check the text that will actually render.
 */
export function autoContrastLabel(
  background: string,
  threshold: number,
  white = "#ffffff",
  black: string,
): string {
  return relativeLuminance(background) > threshold ? black : white;
}
